import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Route } from 'react-router-dom';
import userIsAuthenticated from '../utils/authentication';

/**
 * Stable Route renderer for feature modules. Uses a bound `render` callback so
 * React Router does not remount the module when parent views re-render (e.g.
 * jwtToken updates). Props passed via `injectedProps` still update in place;
 * modules should refetch in componentDidUpdate when business data changes.
 */
class ModuleRouteBound extends Component {
  constructor(props) {
    super(props);
    this.renderModuleRoute = this.renderModuleRoute.bind(this);
    this.resolvedForComponent = null;
    this.resolvedForAuthenticated = null;
    this.ResolvedComponent = null;
    this.syncResolvedComponent(props);
  }

  /**
   * Memoized resolution of the wrapped component. We recompute (and thus wrap
   * with the auth HOC) only when `component`/`authenticated` actually change, so
   * modules are not remounted on unrelated prop/token updates. Crucially this
   * runs during render — not in componentDidUpdate — because <Switch> reuses this
   * instance across routes that share the WrapperView type (e.g. /profile <->
   * a module page). Resolving in componentDidUpdate left ResolvedComponent stale
   * for the render that mattered, so the wrapper kept showing the previous screen.
   */
  syncResolvedComponent(props) {
    const { component, authenticated } = props;
    if (this.resolvedForComponent === component
      && this.resolvedForAuthenticated === authenticated
      && this.ResolvedComponent) {
      return;
    }
    this.resolvedForComponent = component;
    this.resolvedForAuthenticated = authenticated;
    this.ResolvedComponent = authenticated ? userIsAuthenticated(component) : component;
  }

  shouldRenderModule() {
    const { requireRoleInQuery, authenticated, locationSearch } = this.props;
    if (requireRoleInQuery && authenticated) {
      const roleInUrl = new URLSearchParams(locationSearch || '').get('role');
      if (!roleInUrl) {
        return false;
      }
    }
    return true;
  }

  renderModuleRoute(routeProps) {
    if (!this.shouldRenderModule()) {
      return null;
    }
    const { ResolvedComponent } = this;
    const { injectedProps } = this.props;
    return <ResolvedComponent {...routeProps} {...injectedProps} />;
  }

  render() {
    // Resolve at render time so a reused instance (Switch shares the WrapperView
    // type across routes) renders the correct component immediately.
    this.syncResolvedComponent(this.props);
    return <Route render={this.renderModuleRoute} />;
  }
}

ModuleRouteBound.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  authenticated: PropTypes.bool,
  locationSearch: PropTypes.string,
  requireRoleInQuery: PropTypes.bool,
  injectedProps: PropTypes.shape({}),
};

ModuleRouteBound.defaultProps = {
  authenticated: false,
  locationSearch: '',
  requireRoleInQuery: false,
  injectedProps: {},
};

export default ModuleRouteBound;
